import { ReactComponent as LeftArrow } from "../../assets/icons/leftArrow.svg";
import { ReactComponent as RightArrow } from "../../assets/icons/rightArrow.svg";
import { ReactComponent as Refresh } from "../../assets/icons/refresh.svg";

import Input from "../../components/common/input";
import SelectBox from "../../components/common/selectBox";
import Button from "../../../common/components/button";
import NewMember from "./-components/NewMember";
import MemberTable from "./-components/MemberTable";
import { useGetMember } from "../../../common/hooks/console/member/useGetMember";
import React, { useEffect, useState } from "react";
import ModifyMember from "./-components/ModifyMember";
import { useAuthStore } from "../../store/useAuthStore";
import Spinner from "../../../common/components/spinner";

const Member = () => {
    const level = useAuthStore(state => state.m_level);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [currentPage, setCurrentPage] = useState<number | null>(1);
    const [search, setSearch] = useState<string>("m_name");
    const [searchTxt, setSearchTxt] = useState<string>("");
    const [perPage, setPerPage] = useState<number>(20);
    const [newMember, setNewMember] = useState<boolean>(false);
    const [field, setField] = useState<string>("reg_date");
    const [direction, setDirection] = useState<"ASC" | "DESC">("DESC");
    const [selectedMemberRefetch, setSelectedMemberRefetch] = useState<(() => Promise<any>) | null>(null);
    const storedMemberIdx = localStorage.getItem("member_idx");
    const initialMemberIdx = storedMemberIdx ? Number(storedMemberIdx) : 0;
    const [selectedMemberIdx, setSelectedMemberIdx] = useState<number | null>(initialMemberIdx);

    const {
        data: listData,
        refetch: refetchList,
        isLoading: isListLoading,
    } = useGetMember(currentPage ?? 1, perPage, search, searchTxt, field, direction);

    const maxPage = listData?.last_page ?? 1;
    const handleRefresh = async () => {
        setIsRefreshing(true);
        const startTime = Date.now();

        await Promise.all([refetchList(), selectedMemberRefetch ? selectedMemberRefetch() : Promise.resolve()]);

        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(100 - elapsedTime, 0);

        setTimeout(() => {
            setIsRefreshing(false);
        }, remainingTime);
    };

    const handlePageInput = (value: string) => {
        if (value.trim() === "") {
            setCurrentPage(null);
            return;
        }
        if (!/^\d*$/.test(value)) {
            return;
        }
        const newPage = Number(value.trim());
        if (isNaN(newPage) || newPage < 1 || newPage > maxPage) {
            alert("페이지 범위를 벗어납니다.");
            return;
        }
        setCurrentPage(newPage);
    };
    const handleSetRefetch = (refetchFn: () => Promise<any>) => {
        setSelectedMemberRefetch(() => refetchFn);
    };

    const handleSort = (newField: string) => {
        setField(prevField => {
            if (prevField === newField) {
                setDirection(prevDirection => (prevDirection === "ASC" ? "DESC" : "ASC"));
                return prevField;
            } else {
                setDirection("ASC");
                return newField;
            }
        });
    };
    useEffect(() => {
        if (initialMemberIdx) {
            localStorage.removeItem("member_idx");
        }
    }, [initialMemberIdx]);
    return (
        <div className="flex">
            {(isListLoading || isRefreshing) && <Spinner />}
            <div className="flex w-full flex-col overflow-hidden h-dvh">
                {/* Header */}
                <div className="flex justify-between p-3 items-center border-b border-gray4">
                    <p className="text-2xl">회원목록</p>
                    <div className="flex items-center gap-2">
                        <button onClick={() => currentPage && currentPage > 1 && setCurrentPage(currentPage - 1)}>
                            <LeftArrow />
                        </button>
                        <Input
                            inputClassName="text-center"
                            className="w-20 text-center outline-0 text-sm flex"
                            value={currentPage !== null ? currentPage.toString() : ""}
                            onChange={e => handlePageInput(e.target.value)}
                        />
                        <p className="text-gray3">/ {maxPage}</p>
                        <button onClick={() => currentPage && currentPage < maxPage && setCurrentPage(currentPage + 1)}>
                            <RightArrow />
                        </button>
                    </div>
                </div>

                {/* Title */}
                <div className="flex justify-between p-3 items-center border-b border-gray4">
                    <div className="flex gap-1 items-center ">
                        <p className="text-xs">총</p>
                        <button className="text-sm font-bold text-primary2">{listData?.total_count.toLocaleString() ?? null}</button>
                    </div>
                    <div className="flex gap-2 items-center">
                        <Refresh className="cursor-pointer" onClick={handleRefresh} />
                        {level !== 7 && (
                            <Button
                                onClick={() => {
                                    setNewMember(true);
                                }}
                                size="S"
                                className="px-2"
                                status="regist"
                            >
                                회원정보 신규등록
                            </Button>
                        )}

                        <SelectBox
                            status="none"
                            className="w-[100px]"
                            options={[
                                { value: "20", label: "20건씩" },
                                { value: "50", label: "50건씩" },
                                { value: "100", label: "100건씩" },
                            ]}
                            value={perPage.toString()}
                            onChange={value => setPerPage(Number(value))}
                        />
                        <div className="w-[240px] flex gap-1">
                            <SelectBox
                                status="none"
                                className="min-w-[100px]"
                                options={[
                                    { value: "m_name", label: "회원명" },
                                    { value: "customer_name", label: "고객명" },
                                ]}
                                value={search}
                                onChange={(value: string) => setSearch(value)}
                            />
                            <Input
                                placeholder="검색"
                                status="none"
                                className=""
                                value={searchTxt}
                                onChange={e => setSearchTxt(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                    <MemberTable
                        onSort={handleSort}
                        newMember={newMember}
                        data={listData?.data_list || []}
                        onRowClick={memberIdx => {
                            setSelectedMemberIdx(memberIdx);
                            setNewMember(false);
                        }}
                        selectedMemberIdx={selectedMemberIdx || undefined}
                    />
                </div>
            </div>

            <div className="md:min-w-[620px] min-w-[560px] bg-gray6 p-4 h-dvh">
                {newMember || listData?.data_list.length === 0 ? (
                    <NewMember />
                ) : (
                    <ModifyMember
                        onRefresh={refetchFn => handleSetRefetch(refetchFn)}
                        key={selectedMemberIdx}
                        onComplete={id => {
                            setSelectedMemberIdx(prev => {
                                if (prev === id) {
                                    return listData?.data_list?.[0]?.member_idx ?? null;
                                }
                                return prev;
                            });
                        }}
                        memberIdx={selectedMemberIdx || 0}
                    />
                )}
            </div>
        </div>
    );
};

export default Member;
